import { SchemaPropertyType } from "#gen/abacus/v2/abacus_pb.js";
import { DataType } from "#gen/common/v1/types_pb.js";
import { ChoiceFieldPropertiesSchema, ChoiceType, FieldPropertiesSchema, FieldType, FileUploadPropertiesSchema, TextFieldPropertiesSchema, type ChoiceFieldProperties, type Field, type FieldProperties, type TextFieldProperties } from "#gen/forms/v1/field_pb.js";
import { create } from "#lib/protobuf-compat.js";

interface TypeIcon {
	accentColor: string;
	icon: string;
	iconSet: string;
	title: string;
}

interface Section {
	title: string;
	accentColor: string;
	elements: Element[];
}

interface Element {
	title: string;
	icon: string;
	iconSet: string;
	fieldType: FieldType;
	fieldProperties?: FieldProperties;
	requiredFeatureFlag?: string;
}

export const fieldTypes: Section[] = [
	{
		title: "Contact",
		accentColor: "#CCEAC2",
		elements: [
			{
				title: "Full name",
				iconSet: "heroicons",
				icon: "user",
				fieldType: FieldType.NAME,
			},
			{
				title: "Email",
				iconSet: "heroicons",
				icon: "envelope",
				fieldType: FieldType.EMAIL,
			},
			{
				title: "Address",
				iconSet: "heroicons",
				icon: "home",
				fieldType: FieldType.ADDRESS,
			},
			{
				title: "Phone",
				iconSet: "heroicons",
				icon: "device-phone-mobile",
				fieldType: FieldType.PHONE,
			},
		],
	},
	{
		title: "Choice",
		accentColor: "#D9D2E9",
		elements: [
			{
				title: "Dropdown",
				iconSet: "heroicons",
				icon: "queue-list",
				fieldType: FieldType.CHOICE,
				fieldProperties: create(FieldPropertiesSchema, { properties: { case: "choice", value: create(ChoiceFieldPropertiesSchema, { choiceType: ChoiceType.DROPDOWN, choices: [{ value: "Option 1" }] }) } }),
			},
			{
				title: "Single select",
				iconSet: "carbon",
				icon: "radio-button-checked",
				fieldType: FieldType.CHOICE,
				fieldProperties: create(FieldPropertiesSchema, { properties: { case: "choice", value: create(ChoiceFieldPropertiesSchema, { choiceType: ChoiceType.RADIO, choices: [{ value: "Option 1" }] }) } }),
			},
			{
				title: "Multi-select",
				iconSet: "heroicons",
				icon: "list-bullet",
				fieldType: FieldType.CHOICE,
				fieldProperties: create(FieldPropertiesSchema, { properties: { case: "choice", value: create(ChoiceFieldPropertiesSchema, { choiceType: ChoiceType.CHECKBOX, choices: [{ value: "Option 1" }] }) } }),
			},
			{
				title: "Single checkbox",
				iconSet: "fluent",
				icon: "checkbox-checked-16-regular",
				fieldType: FieldType.CHOICE,
				fieldProperties: create(FieldPropertiesSchema, { properties: { case: "choice", value: create(ChoiceFieldPropertiesSchema, { choiceType: ChoiceType.BOOL, choices: [{ value: "I agree to receive future communication emails" }] }) } }),
			},
			{
				title: "Date",
				iconSet: "heroicons",
				icon: "calendar-days",
				fieldType: FieldType.DATE,
			},
		],
	},
	{
		title: "Other",
		accentColor: "#F3F4F6",
		elements: [
			{
				title: "Number",
				iconSet: "heroicons",
				icon: "calculator",
				fieldType: FieldType.NUMBER,
			},
			{
				title: "File upload",
				iconSet: "heroicons",
				icon: "folder-plus",
				fieldType: FieldType.FILE_UPLOAD,
				fieldProperties: create(FieldPropertiesSchema, {
					properties: {
						case: "fileUpload",
						value: create(FileUploadPropertiesSchema, { captureImage: false }),
					},
				}),
			},
		],
	},
	{
		title: "Page",
		accentColor: "#94E3DB",
		elements: [
			{
				title: "Header",
				iconSet: "gridicons",
				icon: "heading-h1",
				fieldType: FieldType.BLOCK_TEXT,
			},
			{
				title: "Terms & conditions",
				iconSet: "heroicons",
				icon: "clipboard-document-check",
				fieldType: FieldType.LEGAL,
			},
		],
	},
	{
		title: "Text input",
		accentColor: "#B1CAF6",
		elements: [
			{
				title: "Short text",
				iconSet: "heroicons",
				icon: "bars-2",
				fieldType: FieldType.TEXT,
			},
			{
				title: "Long text",
				iconSet: "heroicons",
				icon: "bars-3-bottom-left",
				fieldType: FieldType.TEXT,
				fieldProperties: create(FieldPropertiesSchema, { properties: { case: "textField", value: create(TextFieldPropertiesSchema, { paragraph: true }) } }),
			},
		],
	},
];

export function getTypeIcon(type: FieldType, properties?: FieldProperties): TypeIcon | undefined {
	for (const element of fieldTypes) {
		for (const subElement of element.elements) {
			const iconType = { accentColor: element.accentColor, icon: subElement.icon, iconSet: subElement.iconSet, title: subElement.title };
			if (isSameTextType(subElement, type, properties)) {
				return iconType;
			} else if (isSameChoiceType(subElement, type, properties)) {
				return iconType;
			} else if (subElement.fieldType === type && type !== FieldType.TEXT && type !== FieldType.CHOICE) {
				return iconType;
			}
		}
	}
	return undefined;
}

function isSameTextType(subElement: Element, type: FieldType, properties?: FieldProperties): boolean {
	if (type === FieldType.TEXT && subElement.fieldType === FieldType.TEXT) {
		if (((properties?.properties?.value as TextFieldProperties)?.paragraph ?? false) === ((subElement.fieldProperties?.properties?.value as TextFieldProperties)?.paragraph ?? false)) {
			return true;
		}
	}
	return false;
}

function isSameChoiceType(subElement: Element, type: FieldType, properties?: FieldProperties): boolean {
	if (type === FieldType.CHOICE && subElement.fieldType === FieldType.CHOICE) {
		if ((properties?.properties?.value as ChoiceFieldProperties)?.choiceType === (subElement.fieldProperties?.properties?.value as ChoiceFieldProperties)?.choiceType) {
			return true;
		}
	}
	return false;
}

export function getDataType(type: SchemaPropertyType): DataType {
	let dataType: DataType;
	switch (type) {
		case SchemaPropertyType.UNSPECIFIED:
			dataType = DataType.STRING;
			break;
		case SchemaPropertyType.NUMBER:
			dataType = DataType.NUMBER;
			break;
		case SchemaPropertyType.STRING:
			dataType = DataType.STRING;
			break;
		case SchemaPropertyType.BOOL:
			dataType = DataType.BOOLEAN;
			break;
		case SchemaPropertyType.STRUCT:
			dataType = DataType.OBJECT;
			break;
		case SchemaPropertyType.LIST:
			dataType = DataType.ARRAY;
			break;
		case SchemaPropertyType.EMAIL:
			dataType = DataType.EMAIL;
			break;
		case SchemaPropertyType.PHONE:
			dataType = DataType.PHONE;
			break;
		case SchemaPropertyType.ADDRESS:
			dataType = DataType.ADDRESS;
			break;
		case SchemaPropertyType.URL:
			dataType = DataType.URL;
			break;
		case SchemaPropertyType.DATE:
			dataType = DataType.DATE;
			break;
		case SchemaPropertyType.OBJECT:
			dataType = DataType.OBJECT;
			break;
		case SchemaPropertyType.ARRAY:
			dataType = DataType.ARRAY;
			break;
		case SchemaPropertyType.NAME_FULL:
			dataType = DataType.NAME;
			break;
		default:
			dataType = DataType.STRING;
			break;
	}
	return dataType;
}

export function getValidFieldTypes(type: SchemaPropertyType): { type: FieldType; choiceType?: ChoiceType }[] {
	switch (type) {
		case SchemaPropertyType.NUMBER:
			return [{ type: FieldType.NUMBER }];
		case SchemaPropertyType.STRING:
			return [{ type: FieldType.TEXT }, { type: FieldType.CHOICE, choiceType: ChoiceType.DROPDOWN }, { type: FieldType.CHOICE, choiceType: ChoiceType.RADIO }, { type: FieldType.BLOCK_TEXT }];
		case SchemaPropertyType.EMAIL:
			return [{ type: FieldType.EMAIL }];
		case SchemaPropertyType.PHONE:
			return [{ type: FieldType.PHONE }];
		case SchemaPropertyType.ADDRESS:
			return [{ type: FieldType.ADDRESS }];
		case SchemaPropertyType.DATE:
			return [{ type: FieldType.DATE }];
		case SchemaPropertyType.ARRAY:
			return [{ choiceType: ChoiceType.CHECKBOX, type: FieldType.CHOICE }];
		case SchemaPropertyType.NAME_FULL:
			return [{ type: FieldType.NAME }];
		case SchemaPropertyType.BOOL:
			return [{ type: FieldType.CHOICE, choiceType: ChoiceType.BOOL }];
		default:
			return [{ type: FieldType.TEXT }];
	}
}

export function getValidPropertyTypeForField(type: FieldType, choiceType?: ChoiceType): SchemaPropertyType {
	switch (type) {
		case FieldType.NUMBER:
			return SchemaPropertyType.NUMBER;
		case FieldType.TEXT:
			return SchemaPropertyType.STRING;
		case FieldType.CHOICE:
			if (choiceType === ChoiceType.CHECKBOX) {
				return SchemaPropertyType.ARRAY;
			} else if (choiceType === ChoiceType.BOOL) {
				return SchemaPropertyType.BOOL;
			} else {
				return SchemaPropertyType.STRING;
			}
		case FieldType.BLOCK_TEXT:
			return SchemaPropertyType.STRING;
		case FieldType.EMAIL:
			return SchemaPropertyType.EMAIL;
		case FieldType.PHONE:
			return SchemaPropertyType.PHONE;
		case FieldType.ADDRESS:
			return SchemaPropertyType.ADDRESS;
		case FieldType.DATE:
			return SchemaPropertyType.DATE;
		case FieldType.NAME:
			return SchemaPropertyType.NAME_FULL;
		default:
			return SchemaPropertyType.STRING;
	}
}

export function isStringField(field: Field) {
	if (field.fieldType === FieldType.CHOICE) {
		return field.properties?.properties.case === "choice" && [ChoiceType.DROPDOWN, ChoiceType.RADIO].includes(field.properties.properties.value?.choiceType);
	} else {
		return [FieldType.TEXT, FieldType.BLOCK_TEXT].includes(field.fieldType);
	}
}
